'use strict';

/**
 * Canonical pricing module — 3-tier graduated per-user/month rates.
 *
 * Shared by:
 *   - src/components/StripeCheckoutForm.js   (frontend display + local calc)
 *   - src/components/CreateAccountForm.js    (consumes /payment-intent breakdown)
 *   - src/lambda/account-request-handler.js  (authoritative server-side calc)
 *
 * Marginal/graduated pricing:
 *   First 50 users        @ $20/user/mo  (Basic tier)
 *   Next 50 (51–100)      @ $15/user/mo  (Popular tier)
 *   Remainder (101+)      @ $10/user/mo  (Ultimate tier)
 *
 * The bill never decreases as users are added; crossing a tier boundary
 * only changes the marginal rate on additional users, not on existing ones.
 *
 * CommonJS export shape so both the React build (webpack) and the raw
 * Lambda zip can `require('./pricing')` without a build step.
 */

const PRICING_TIERS = Object.freeze([
    Object.freeze({ label: 'Basic',    upTo: 50,       ratePerUserCents: 2000 }),
    Object.freeze({ label: 'Popular',  upTo: 100,      ratePerUserCents: 1500 }),
    Object.freeze({ label: 'Ultimate', upTo: Infinity, ratePerUserCents: 1000 }),
]);

/**
 * Total setup fee (in cents) for a given user count under graduated pricing.
 * Returns 0 for non-positive capacity.
 */
function calcSetupFeeCents(capacity) {
    const n = Math.max(0, Math.floor(Number(capacity) || 0));
    if (n === 0) return 0;
    let remaining = n;
    let prev = 0;
    let cents = 0;
    for (const tier of PRICING_TIERS) {
        const bandSize = tier.upTo - prev;
        const slice = Math.min(remaining, bandSize);
        if (slice <= 0) break;
        cents += slice * tier.ratePerUserCents;
        remaining -= slice;
        prev = tier.upTo;
        if (remaining <= 0) break;
    }
    return cents;
}

/**
 * Returns the tier label ("Basic" | "Popular" | "Ultimate") that the
 * *last* (highest) user belongs to for the given capacity.
 */
function tierLabelFor(capacity) {
    const n = Math.max(1, Math.floor(Number(capacity) || 1));
    let prev = 0;
    for (const tier of PRICING_TIERS) {
        if (n <= tier.upTo) return tier.label;
        prev = tier.upTo;
    }
    // Unreachable — last tier upTo is Infinity.
    return PRICING_TIERS[PRICING_TIERS.length - 1].label;
}

/**
 * Per-band slice breakdown for UI display.
 * Returns [{ label, users, ratePerUserCents, subtotalCents }, ...]
 * Only includes bands that actually contain users.
 */
function pricingBreakdown(capacity) {
    const n = Math.max(0, Math.floor(Number(capacity) || 0));
    const out = [];
    let remaining = n;
    let prev = 0;
    for (const tier of PRICING_TIERS) {
        const bandSize = tier.upTo - prev;
        const slice = Math.min(remaining, bandSize);
        if (slice > 0) {
            out.push({
                label: tier.label,
                users: slice,
                ratePerUserCents: tier.ratePerUserCents,
                subtotalCents: slice * tier.ratePerUserCents,
            });
            remaining -= slice;
        }
        prev = tier.upTo;
        if (remaining <= 0) break;
    }
    return out;
}

/**
 * Effective blended per-user rate (cents) for display under graduated pricing.
 * Returns 0 for non-positive capacity.
 */
function effectivePerUserCents(capacity) {
    const n = Math.max(0, Math.floor(Number(capacity) || 0));
    if (n === 0) return 0;
    return Math.round(calcSetupFeeCents(n) / n);
}

/**
 * AWS AppStream hourly rate + users-per-instance for every supported profile.
 * Mirrors account-request-handler.js AWS_RATES so the frontend local-fallback
 * estimate and the server-authoritative escrow calculation are always in sync.
 */
const AWS_RATES = Object.freeze({
    'Office:Basic:Normal':       Object.freeze({ rate: 0.05,  users: 1 }),
    'Office:Basic:Fast':         Object.freeze({ rate: 0.10,  users: 1 }),
    'Office:Basic:Faster':       Object.freeze({ rate: 0.20,  users: 1 }),
    'Office:Advanced:Normal':    Object.freeze({ rate: 0.10,  users: 4 }),
    'Office:Advanced:Fast':      Object.freeze({ rate: 0.16,  users: 8 }),
    'Office:Advanced:Faster':    Object.freeze({ rate: 0.32,  users: 8 }),
    'Education:Basic:Normal':    Object.freeze({ rate: 0.10,  users: 4 }),
    'Education:Basic:Fast':      Object.freeze({ rate: 0.10,  users: 4 }),
    'Education:Basic:Faster':    Object.freeze({ rate: 0.20,  users: 4 }),
    'Education:Advanced:Normal': Object.freeze({ rate: 0.16,  users: 8 }),
    'Education:Advanced:Fast':   Object.freeze({ rate: 0.35,  users: 4 }),
    'Education:Advanced:Faster': Object.freeze({ rate: 0.71,  users: 4 }),
    'Graphics:Basic:Normal':     Object.freeze({ rate: 0.35,  users: 1 }),
    'Graphics:Basic:Fast':       Object.freeze({ rate: 0.71,  users: 1 }),
    'Graphics:Basic:Faster':     Object.freeze({ rate: 1.42,  users: 1 }),
    'Graphics:Advanced:Normal':  Object.freeze({ rate: 1.01,  users: 1 }),
    'Graphics:Advanced:Fast':    Object.freeze({ rate: 2.03,  users: 1 }),
    'Graphics:Advanced:Faster':  Object.freeze({ rate: 4.06,  users: 1 }),
});

/**
 * Escrow/usage deposit (in cents) for the given profile and session duration.
 * Rounds up to the nearest $10.  Mirrors the Lambda calcEscrowDepositCents exactly.
 *
 * @param {number} capacity          - max concurrent users (fleet_max_capacity)
 * @param {string} useCase           - 'Office' | 'Education' | 'Graphics'
 * @param {string} tier              - 'Basic' | 'Advanced'
 * @param {string} speed             - 'Normal' | 'Fast' | 'Faster'
 * @param {number} durationSeconds   - max user session length in seconds
 */
function calcEscrowDepositCents(capacity, useCase, tier, speed, durationSeconds) {
    const profileKey = `${useCase}:${tier}:${speed}`;
    const profile = AWS_RATES[profileKey] || AWS_RATES['Office:Basic:Fast'];
    const hoursPerDay = Math.ceil((durationSeconds || 36000) / 3600);
    const reqInstances = Math.ceil(Math.max(1, Math.floor(Number(capacity) || 1)) / profile.users);
    const peakRaw = reqInstances * profile.rate * hoursPerDay * 22;
    const rawDeposit = peakRaw * 1.58125;
    return Math.ceil(rawDeposit / 10) * 10 * 100;
}

module.exports = {
    PRICING_TIERS,
    AWS_RATES,
    calcSetupFeeCents,
    calcEscrowDepositCents,
    tierLabelFor,
    pricingBreakdown,
    effectivePerUserCents,
};
